import { z } from 'zod';
import emojiRegex from 'emoji-regex';
import { app } from '$lib/stores/app/store';
import { get } from 'svelte/store';

const getLocale = () => get(app).meta.localeCode;
const locale = getLocale();

/**
 * @type {Record<string, Record<string, string>>}
 */
const messages = {
	'en-US': {
		enterEmail: 'Please enter an email address.',
		invalidEmail: 'Email format you entered is invalid.',
		confirmEmail: 'Please confirm your email address.',
		enterPassword: 'Please enter a password.',
		passwordLength: 'Password must be at least 5 characters.',
		confirmPassword: 'Please confirm your password.',
		cardholderName: 'Please enter a valid cardholder name.',
		cardholderNoEmoji: 'Please enter a valid cardholder name without emojis.',
		enterCardNumber: 'Please enter a card number.',
		invalidCardNumber: 'Your card number is invalid.',
		enterExpDate: 'Please enter an exp. date.',
		invalidExpDate: 'Please enter a valid date.',
		enterCvv: 'Please enter a CVV',
		invalidCvv: 'Your CVV is invalid.',
		enterZip: 'Please enter a zip/postal code.',
		invalidZip: 'Your zip code is invalid.',
		agreeToTerms: 'Please check the box to indicate that you agree to these terms.'
	},
	'es-US': {
		enterEmail: 'Por favor, escriba una dirección de correo electrónico.',
		invalidEmail: 'El formato del correo electrónico ingresado no es válido.',
		confirmEmail: 'Por favor, confirme su dirección de correo electrónico.',
		enterPassword: 'Por favor, escriba una contraseña.',
		passwordLength: 'La contraseña debe tener al menos 5 caracteres.',
		confirmPassword: 'Por favor, confirme su contraseña.',
		cardholderName: 'Por favor, ingrese un nombre de titular válido.',
		cardholderNoEmoji: 'Por favor, ingrese un nombre de titular válido sin emojis.',
		enterCardNumber: 'Por favor, ingrese un número de tarjeta.',
		invalidCardNumber: 'El número de tarjeta no es válido.',
		enterExpDate: 'Por favor, ingrese una fecha de vencimiento.',
		invalidExpDate: 'Por favor, ingrese una fecha válida.',
		enterCvv: 'Por favor, ingrese el CVV',
		invalidCvv: 'El CVV no es válido.',
		enterZip: 'Por favor, ingrese un código postal.',
		invalidZip: 'El código postal no es válido.',
		agreeToTerms:
			'Por favor, marque la casilla para indicar que está de acuerdo con estos términos.'
	}
};

/**
 * @param {string} key
 * @param {string} locale
 * @returns {string}
 */
const getMessage = (key, locale = 'en-US') => {
	return messages[locale]?.[key] ?? messages['en-US'][key];
};

/**
 * @param {z.ZodSchema} zodSchema - The zod schema to validate the value against
 * @returns {(value: string) => { message: string; } | null}
 */
export const validator = (zodSchema) => (value) => {
	const result = zodSchema.safeParse(value);
	if (!result.success) {
		const firstError = result.error.issues[0];
		return { message: firstError.message };
	}
	return null;
};

/**
 * @param {string} valueToMatch - The value to match against
 * @returns {(value: string, form: Record<string, { value: string; }>) => { message: string; } | null}
 */
export const valueMatch = (valueToMatch) => (value, form) => {
	const pascalCaseVal = valueToMatch.charAt(0).toUpperCase() + valueToMatch.slice(1);
	return value === form[valueToMatch]?.value
		? null
		: { message: `${pascalCaseVal}s do not match.` };
};

export const email = z
	.string()
	.min(1, { message: getMessage('enterEmail', locale) })
  .email({ message: getMessage('invalidEmail', locale) });

export const confirmEmail = z.string().min(1, {
	message: getMessage('confirmEmail', locale)
});

export const loginPassword = z
	.string()
	.min(1, { message: getMessage('enterPassword', locale) });

export const password = z
	.string()
	.min(1, { message: getMessage('enterPassword', locale) })
	.min(5, { message: getMessage('passwordLength', locale) })

export const confirmPassword = z
	.string()
	.min(1, { message: getMessage('confirmPassword', locale) });

export const cardName = z
	.string()
	.min(1, { message: getMessage('cardholderName', locale) })
	.refine((value) => !emojiRegex().test(value), {
		message: getMessage('cardholderNoEmoji', locale)
	});

export const cardNumber = z
	.string()
	.min(1, { message: getMessage('enterCardNumber', locale) })
	.refine((data) => data !== '0', { message: getMessage('invalidCardNumber', locale) });

export const expDate = z
	.string()
	.min(1, { message: getMessage('enterExpDate', locale) })
	.regex(/^(0[1-9]|1[0-2])(\/?)[0-9]{2}$/, { message: getMessage('invalidExpDate', locale) });

export const cardCvv = z
	.string()
	.min(1, { message: getMessage('enterCvv', locale) })
	.refine((value) => !emojiRegex().test(value), { message: getMessage('invalidCvv', locale) })
	.refine((data) => data !== '0', { message: getMessage('invalidCvv', locale) });

export const zipCode = z
	.string()
	.min(1, { message: getMessage('enterZip', locale) })
	.refine((value) => !emojiRegex().test(value), { message: getMessage('invalidZip', locale) })
	.refine((data) => data !== '0', { message: getMessage('invalidZip', locale) });

export const legalCheckbox = z.string().refine((v) => v === 'checked', {
	message: 'Please check the box to indicate that you agree to these terms.'
});

export const emailCheckbox = z.string().refine((v) => v === 'checked', {
	message: 'You must agree to receive emails from ABCmouse.com.'
});